import React from "react";
import { ContentState, EditorState } from "draft-js";
import { useDispatch, useSelector } from "react-redux";
import { SbiHistoryEntry, SbiResult, State } from "../../../type";
import { postWithTimeout, RequestTimeoutError } from "../../../util/APIService";
import { sessionExpired } from "../../../redux/slices/AuthSlice";

interface AnalyzeResult {
  editorState: EditorState;
  restructuredText: string;
  sbiResult: SbiResult;
}

interface UseSbiAnalysisReturn {
  aiHistoryMap: Record<string, SbiHistoryEntry[]>;
  activeSbiMap: Record<string, SbiResult | null>;
  analyzingMap: Record<string, boolean>;
  restructuringMap: Record<string, boolean>;
  aiBaselineTextMap: Record<string, string>;
  errorToast: string | null;
  dismissError: () => void;
  analyze: (fieldKey: string, rawText: string) => Promise<AnalyzeResult | null>;
  undo: (fieldKey: string) => { editorState: EditorState; text: string } | null;
  clearSbiChips: (fieldKey: string, currentPlainText: string) => void;
}

const ERROR_MESSAGE = "We couldn't analyze your feedback. Please try again or continue without analysis.";
const TIMEOUT_MESSAGE = "Analysis is taking too long. Please try again in a moment.";

export const useSbiAnalysis = (): UseSbiAnalysisReturn => {
  const dispatch = useDispatch();
  const token = useSelector((state: State) => state.auth?.token);

  const [aiHistoryMap, setAiHistoryMap] = React.useState<Record<string, SbiHistoryEntry[]>>({});
  const [activeSbiMap, setActiveSbiMap] = React.useState<Record<string, SbiResult | null>>({});
  const [analyzingMap, setAnalyzingMap] = React.useState<Record<string, boolean>>({});
  const [restructuringMap, setRestructuringMap] = React.useState<Record<string, boolean>>({});
  const [aiBaselineTextMap, setAiBaselineTextMap] = React.useState<Record<string, string>>({});
  const [errorToast, setErrorToast] = React.useState<string | null>(null);

  const dismissError = () => setErrorToast(null);

  const analyze = async (fieldKey: string, rawText: string): Promise<AnalyzeResult | null> => {
    const storeAccessor = {
      getState: () => ({ auth: { token } }),
      dispatch: (action: any) => dispatch(action),
    };

    setAnalyzingMap((prev) => ({ ...prev, [fieldKey]: true }));
    setRestructuringMap((prev) => ({ ...prev, [fieldKey]: true }));

    try {
      const [restructureResponse, sbiResponse] = await Promise.all([
        postWithTimeout(
          "api/v1/user/feedback/restructure",
          { feedbackText: rawText },
          8000,
          storeAccessor
        ),
        postWithTimeout(
          "api/v1/user/feedback/analyze-sbi",
          { feedbackText: rawText },
          8000,
          storeAccessor
        ),
      ]);

      if (!restructureResponse || !restructureResponse.ok) throw new Error("Restructure failed");
      if (!sbiResponse || !sbiResponse.ok) throw new Error("SBI analysis failed");

      const restructureResult = await restructureResponse.json();
      const sbiResult_raw = await sbiResponse.json();

      const editorState = EditorState.createWithContent(
        ContentState.createFromText(restructureResult.restructuredText)
      );

      const sbiResult: SbiResult = {
        situationPresent: sbiResult_raw.situationPresent,
        behaviorPresent: sbiResult_raw.behaviorPresent,
        impactPresent: sbiResult_raw.impactPresent,
      };

      setAiHistoryMap((prev) => ({
        ...prev,
        [fieldKey]: [
          ...(prev[fieldKey] ?? []),
          {
            text: rawText,
            sbiResult: { situationPresent: false, behaviorPresent: false, impactPresent: false },
          },
        ],
      }));
      setActiveSbiMap((prev) => ({ ...prev, [fieldKey]: sbiResult }));
      setAiBaselineTextMap((prev) => ({ ...prev, [fieldKey]: restructureResult.restructuredText }));

      return { editorState, restructuredText: restructureResult.restructuredText, sbiResult };
    } catch (error: unknown) {
      const message = error instanceof RequestTimeoutError ? TIMEOUT_MESSAGE : ERROR_MESSAGE;
      setErrorToast(message);
      return null;
    } finally {
      setAnalyzingMap((prev) => ({ ...prev, [fieldKey]: false }));
      setRestructuringMap((prev) => ({ ...prev, [fieldKey]: false }));
    }
  };

  const undo = (fieldKey: string): { editorState: EditorState; text: string } | null => {
    const history = aiHistoryMap[fieldKey] ?? [];
    if (history.length === 0) return null;

    const previous = history[history.length - 1];
    const remaining = history.slice(0, -1);

    const editorState = EditorState.createWithContent(ContentState.createFromText(previous.text));

    setAiHistoryMap((prev) => ({ ...prev, [fieldKey]: remaining }));
    setActiveSbiMap((prev) => ({ ...prev, [fieldKey]: null }));
    setAiBaselineTextMap((prev) => ({ ...prev, [fieldKey]: "" }));

    return { editorState, text: previous.text };
  };

  const clearSbiChips = (fieldKey: string, currentPlainText: string) => {
    const baselinePlain = (aiBaselineTextMap[fieldKey] ?? "").replace(/\s/g, "");
    const currentPlain = currentPlainText.replace(/\s/g, "");
    if (currentPlain !== baselinePlain) {
      setActiveSbiMap((prev) => ({ ...prev, [fieldKey]: null }));
    }
  };

  return {
    aiHistoryMap,
    activeSbiMap,
    analyzingMap,
    restructuringMap,
    aiBaselineTextMap,
    errorToast,
    dismissError,
    analyze,
    undo,
    clearSbiChips,
  };
};
